import * as React from 'react';
import * as _ from 'lodash';
import { connect } from 'react-redux';
import {
    HealthProblemContext,
    Project,
    PanelType,
    Question,
    AddCondition,
    ConditionType,
    CONDITION_TYPES,
    PreCondition,
    PostCondition,
    QTYPES,
    QuestionSummary,
    PROJECT_EDIT_STATUS
} from '@nexxt/common/types';
import ConditionHeader from './conditonHeader';
import { defaultCondition } from './default';
import AddConditionContainer from './addCondition/AddConditionContainer';
import { AddConditionButton } from './ConditionCommon';
import { IApplicationState } from '../../../../reducers';
import {
    getQuestionByQuestionId,
    saveConditions,
    getConditions,
    deleteConditions
} from '../../../../services/question.service';
import {
    IConditionError,
    IConditionErrors
} from '../../../../interface/conditions.interface';
import { CONDITION_QTYPES } from '../../../../constants/question';
import Conditions from './conditions/Conditions';
import Loader from '../../../common/Loader/Loader';
import NotifyHOC, { IToastNotification } from '../../../../HOC/Notifications';
import { getConfirmationBox } from '../../../common/swal/Sweetalert';
import {
    flattenQuestionSummary,
    getQuestionSummariesWithBuiltIn,
    parseQuestionSummaryToObjects
} from '../../../../utils/question';
import { alertMessage } from '../../../../constants/alertMessage';
import { sortHealthProblemByQuestionID } from '../../../../utils/project';
import Select from 'react-select';
import {
    clearEditedStatus,
    setEditedStatus
} from '../../../../services/utils.service';
import ValidationService from '@nexxt/common/services/ValidationService';
import AutoSavingService from '../../../../utils/AutoSavingService';

interface DispatchProps {
    getQuestionById: (questionId: number, options) => Promise<any>;
    getConditions: (questionId) => Promise<any>;
    saveConditions: (
        questionId: number,
        conditions,
        isPrecondition?: boolean
    ) => Promise<any>;
    deleteConditions: (questionId: number, conditionIds) => Promise<any>;
    setEditedStatus: (
        type: string,
        status?: PROJECT_EDIT_STATUS
    ) => Promise<any>;
    clearEditedStatus: () => Promise<any>;
}

export interface StateProps {
    questionSummaries: QuestionSummary[];
    currentQuestionId: number;
    currentQuestion: Question;
    editEnabled: boolean;
    projectHealth: any;
    currentProject: Project;
    isEdited: string;
    editStatus: PROJECT_EDIT_STATUS;
}

export interface HOCProps {
    toastNotification: IToastNotification;
}

export type ConditionContainerProps = StateProps & DispatchProps & HOCProps;

export interface ConditionContainerState {
    isLoading: boolean;
    isSaving: boolean;
    isAdd: boolean;
    isEdit: boolean;
    editType: ConditionType;
    editCondition: AddCondition | null;
    editIndex: number;
    editRuleIndex: number;
    addCondition: AddCondition | null;
    addErrors: IConditionError;
    preconditionsErrors: IConditionErrors;
    postconditionsErrors: IConditionErrors;
    preconditions: PreCondition[];
    postconditions: PostCondition[];
    filteredQuestionSummaries: QuestionSummary[];
    flattenQuestionSummaries: QuestionSummary[];
    activeQuestionId: number;
}

class ConditionContainer extends React.Component<
    ConditionContainerProps,
    ConditionContainerState
> {
    private initialCondition;
    private validationService;
    constructor(props: ConditionContainerProps) {
        super(props);
        this.validationService = new ValidationService();
        this.state = {
            isLoading: false,
            isSaving: false,
            isAdd: false,
            isEdit: false,
            editIndex: -1,
            editRuleIndex: -1,
            editType: null,
            editCondition: null,
            addCondition: null,
            addErrors: { isError: false },
            preconditionsErrors: { isError: false },
            postconditionsErrors: { isError: false },
            preconditions: [],
            postconditions: [],
            filteredQuestionSummaries: [],
            flattenQuestionSummaries: [],
            activeQuestionId: this.props.currentQuestionId
        };
    }

    async componentDidMount() {
        if (!this.props.currentQuestionId) {
            return;
        }
        this.setState({ isLoading: true });
        await this.fetchCondition();
    }

    fetchCondition = async () => {
        const conditions = this.props.getConditions(
            this.state.activeQuestionId || 0
        );
        try {
            let isAdd = false;
            let addCondition = null;
            const responses = await Promise.resolve(conditions);

            const preconditions = responses.preconditions;
            const postconditions = responses.postconditions;
            if (preconditions.length === 0 && postconditions.length == 0) {
                isAdd = true;
                addCondition = {
                    conditions: defaultCondition(CONDITION_TYPES.PRECONDITION),
                    index: 0,
                    type: CONDITION_TYPES.PRECONDITION
                };
            }
            this.initialCondition = _.cloneDeep({
                preconditions,
                postconditions
            });
            this.setState({
                preconditions,
                postconditions,
                isAdd,
                addCondition,
                isLoading: false
            });
        } catch (e) {
            this.setState({
                isLoading: false
            });
        }
        const language = this.props.currentProject?.languages?.length > 1;
        const questionSummaries = flattenQuestionSummary(
            getQuestionSummariesWithBuiltIn(this.props.questionSummaries, {
                language,
                panel:
                    this.props.currentProject?.panelSettings?.panelType !==
                    PanelType.INCA
            })
        );
        this.setState({
            filteredQuestionSummaries: questionSummaries.filter((q) =>
                CONDITION_QTYPES.includes(q.type as QTYPES)
            ),
            flattenQuestionSummaries: questionSummaries
        });
    };

    /** Validate new condition */
    validateAdd = () => {
        const condition = this.state.addCondition.conditions;
        const type = this.state.addCondition.type;
        const errorObject = this.validationService.validateSingleCondition(
            type,
            condition
        );
        this.setState({
            addErrors: errorObject
        });
        return !errorObject.isError;
    };

    /** Validate pre-conditions */
    validatePreConditions = () => {
        const conditions = this.state.preconditions;
        const errorObject = this.validationService.validateAllConditions(
            CONDITION_TYPES.PRECONDITION,
            conditions
        );
        this.setState({
            preconditionsErrors: errorObject
        });
        return !errorObject.isError;
    };

    /** Validate post-conditions */
    validatePostConditions = () => {
        const conditions = this.state.postconditions;
        const errorObject = this.validationService.validateAllConditions(
            CONDITION_TYPES.PRECONDITION,
            conditions
        );
        this.setState({
            postconditionsErrors: errorObject
        });
        return !errorObject.isError;
    };

    /**=========================================================
     * Add Condition============================================
     *========================================================*/

    onAddCondition = () => {
        this.setState((prevState) => {
            const index = prevState.preconditions.length;
            return {
                isAdd: true,
                addCondition: {
                    conditions: defaultCondition(CONDITION_TYPES.PRECONDITION),
                    index,
                    type: CONDITION_TYPES.PRECONDITION
                }
            };
        }, this.validateAdd);
    };

    onConditionTypeChange = (type: ConditionType) => {
        this.setState((prevState) => {
            let index = prevState.postconditions.length;
            if (type === CONDITION_TYPES.PRECONDITION) {
                index = prevState.preconditions.length;
            }
            return {
                addCondition: {
                    conditions: defaultCondition(type),
                    index,
                    type
                },
                editRuleIndex: -1,
                editIndex: -1
            };
        }, this.validateAdd);
    };

    updateAddCondition = (
        type: ConditionType,
        conditions: PreCondition | PostCondition,
        others: any = {}
    ) => {
        if (!this.props.isEdited && others.editRuleIndex === -1) {
            this.props.setEditedStatus('questionCondition');
        }
        this.setState(
            (prevState) => ({
                addCondition: {
                    ...prevState.addCondition,
                    conditions
                },
                ...others
            }),
            this.validateAdd
        );
    };

    // @TODO: clean up and document
    // the logic here is not clear
    onSaveAddCondition = () => {
        // validation required
        // @TODO: what is this validating?
        // we should return human-readable diagnostic msg
        let currentType = null;
        if (this.validateAdd()) {
            this.setState(
                (prevState) => {
                    const state: any = {
                        addCondition: {
                            conditions: defaultCondition(
                                CONDITION_TYPES.PRECONDITION
                            ),
                            type: CONDITION_TYPES.PRECONDITION
                        },
                        isAdd: false,
                        editRuleIndex: -1
                    };
                    currentType = prevState.addCondition.type;
                    if (
                        prevState.addCondition.type ===
                        CONDITION_TYPES.PRECONDITION
                    ) {
                        state.preconditions = [
                            ...prevState.preconditions,
                            prevState.addCondition.conditions
                        ];
                    } else {
                        state.postconditions = [
                            ...prevState.postconditions,
                            prevState.addCondition.conditions as PostCondition
                        ];
                    }
                    return state;
                },
                () => {
                    // this.props.clearEditedStatus()
                    if (currentType === CONDITION_TYPES.PRECONDITION) {
                        this.props
                            .saveConditions(
                                this.state.activeQuestionId,
                                this.state.preconditions,
                                true
                            )
                            .then(() => {
                                this.props.clearEditedStatus();
                                // show save message
                                this.props.toastNotification.success(
                                    'Conditions saved successfully'
                                );
                                this.props
                                    .getConditions(this.state.activeQuestionId)
                                    .then((response) => {
                                        this.setState({
                                            preconditions:
                                                response.preconditions
                                        });

                                        this.initialCondition = _.cloneDeep({
                                            ...this.initialCondition,
                                            preconditions:
                                                response.preconditions
                                        });
                                    });
                            });
                    } else {
                        this.props
                            .saveConditions(
                                this.state.activeQuestionId,
                                this.state.postconditions
                            )
                            .then(() => {
                                this.props.clearEditedStatus();
                                // show save message
                                this.props.toastNotification.success(
                                    'Conditions saved successfully'
                                );
                                this.props
                                    .getConditions(this.state.activeQuestionId)
                                    .then((response) => {
                                        this.setState({
                                            postconditions:
                                                response.postconditions
                                        });

                                        this.initialCondition = _.cloneDeep({
                                            ...this.initialCondition,
                                            postconditions:
                                                response.postconditions
                                        });
                                    });
                            });
                    }
                }
            );
        } else {
            // @TODO: handle this properly
            this.props.toastNotification.error('Unable to save conditions. ');
        }
    };

    onCancelAddCondition = () => {
        if (this.props.isEdited) {
            this.props.clearEditedStatus();
        }
        this.setState({
            addCondition: null,
            editIndex: -1,
            editRuleIndex: -1,
            isAdd: false,
            isEdit: false,
            editType: null
        });
    };

    onCancelAddRule = () => {
        const conditionType = this.state.editType;
        if (this.state.isAdd) {
            this.setState(
                {
                    ...this.state,
                    addCondition: {
                        ...this.state.addCondition,
                        conditions: {
                            ...this.state.addCondition.conditions,
                            rules: this.state.addCondition.conditions.rules.slice(
                                0,
                                this.state.addCondition.conditions.rules
                                    .length - 1
                            )
                        }
                    },
                    editRuleIndex: -1
                },
                this.validateAdd
            );
        }

        if (this.state.isEdit) {
            if (conditionType === CONDITION_TYPES.PRECONDITION) {
                this.setState(
                    {
                        ...this.state,
                        editRuleIndex: -1,
                        preconditions: _.cloneDeep(
                            this.initialCondition.preconditions
                        )
                    },
                    this.validatePreConditions
                );
            } else if (conditionType === CONDITION_TYPES.POSTCONDITION) {
                this.setState(
                    {
                        ...this.state,
                        editRuleIndex: -1,
                        postconditions: _.cloneDeep(
                            this.initialCondition.postconditions
                        )
                    },
                    this.validatePostConditions
                );
            }
        }
    };
    /**=========================================================
     * Add Condition End========================================
     *========================================================*/

    /**=========================================================
     * Pre Condition============================================
     *========================================================*/

    updateEditCondition = (
        type: ConditionType,
        conditions: (PreCondition & Partial<PostCondition>)[],
        others: any = {}
    ) => {
        if (type === CONDITION_TYPES.PRECONDITION) {
            this.setState(
                {
                    preconditions: [...conditions],
                    ...others
                },
                this.validatePreConditions
            );
        } else {
            this.setState(
                {
                    postconditions: [...(conditions as PostCondition[])],
                    ...others
                },
                this.validatePostConditions
            );
        }
    };

    onSaveEditCondition = (type: ConditionType) => {
        // validation required

        if (
            type === CONDITION_TYPES.PRECONDITION
                ? this.validatePreConditions()
                : this.validatePostConditions()
        ) {
            if (this.props.isEdited) {
                this.props.clearEditedStatus();
            }
            this.setState(
                (prevState) => {
                    const state: any = {
                        isAdd: false,
                        isEdit: false,
                        editRuleIndex: -1,
                        editIndex: -1,
                        editCondition: null
                    };
                    return state;
                },
                () => {
                    if (type === CONDITION_TYPES.PRECONDITION) {
                        this.props
                            .saveConditions(
                                this.state.activeQuestionId,
                                this.state.preconditions,
                                true
                            )
                            .then(() => {
                                // show save message
                                this.props.toastNotification.success(
                                    'Conditions saved successfully'
                                );
                                this.props
                                    .getConditions(this.state.activeQuestionId)
                                    .then((response) => {
                                        this.setState({
                                            preconditions:
                                                response.preconditions
                                        });
                                        this.initialCondition = _.cloneDeep({
                                            ...this.initialCondition,
                                            preconditions:
                                                response.preconditions
                                        });
                                    });
                            });
                    } else {
                        this.props
                            .saveConditions(
                                this.state.activeQuestionId,
                                this.state.postconditions,
                                false
                            )
                            .then(() => {
                                // show save message
                                this.props.toastNotification.success(
                                    'Conditions saved successfully'
                                );
                                this.props
                                    .getConditions(this.state.activeQuestionId)
                                    .then((response) => {
                                        this.setState({
                                            postconditions:
                                                response.postconditions
                                        });
                                        this.initialCondition = _.cloneDeep({
                                            ...this.initialCondition,
                                            postconditions:
                                                response.postconditions
                                        });
                                    });
                            });
                    }
                }
            );
        }
    };

    onEditCondition = (type: ConditionType, index: number) => {
        if (this.state.isAdd || this.state.isEdit) {
            // show message
            this.props.toastNotification.error(
                'Please finish editing condition before you proceed'
            );
        } else {
            if (!this.props.isEdited) {
                this.props.setEditedStatus('questionCondition');
            }
            const editCondition: any = _.cloneDeep(
                type === CONDITION_TYPES.PRECONDITION
                    ? this.state.preconditions[index]
                    : (this.state.postconditions[index] as any)
            );
            this.setState({
                isEdit: true,
                editIndex: index,
                editType: type,
                editCondition
            });
        }
    };

    onCancelEditCondition = () => {
        if (this.props.isEdited) {
            this.props.clearEditedStatus();
        }
        if (this.state.editType === CONDITION_TYPES.PRECONDITION) {
            const conditions = this.state.preconditions;
            conditions.splice(
                this.state.editIndex,
                1,
                this.state.editCondition as any
            );
            this.setState({
                preconditions: conditions,
                editCondition: null,
                editIndex: -1,
                editRuleIndex: -1,
                isAdd: false,
                isEdit: false,
                editType: null
            });
        } else {
            const conditions = this.state.postconditions;
            conditions.splice(
                this.state.editIndex,
                1,
                this.state.editCondition as any
            );
            this.setState({
                postconditions: conditions,
                editCondition: null,
                editIndex: -1,
                editRuleIndex: -1,
                isAdd: false,
                isEdit: false,
                editType: null
            });
        }
    };

    onEditRule = (ruleIndex: number) => {
        if (this.state.editRuleIndex === -1) {
            this.setState({
                editRuleIndex: ruleIndex
            });
        } else {
            // show message
            this.props.toastNotification.error(
                'Please finish editing condition rules before you proceed'
            );
        }
    };

    onDeleteConditions = async (type: ConditionType, index) => {
        const result = await getConfirmationBox({
            message: alertMessage.CONFIRM_DELETE_ITEM,
            showCloseButton: true,
            confirmButtonColor: '',
            title: alertMessage.DELETE_ITEM
        });
        if (result.value) {
            const condition =
                type === CONDITION_TYPES.PRECONDITION
                    ? this.state.preconditions[index]
                    : this.state.postconditions[index];
            if (type === CONDITION_TYPES.PRECONDITION) {
                const conditions = [...this.state.preconditions];
                conditions.splice(index, 1);
                this.setState({
                    preconditions: conditions
                });
                this.initialCondition = _.cloneDeep({
                    ...this.initialCondition,
                    preconditions: conditions
                });
            } else {
                const conditions = [...this.state.postconditions];
                conditions.splice(index, 1);
                this.setState({
                    postconditions: conditions
                });
                this.initialCondition = _.cloneDeep({
                    ...this.initialCondition,
                    postconditions: conditions
                });
            }
            this.props
                .deleteConditions(
                    this.state.activeQuestionId,
                    condition.conditionID
                )
                .then(() => {
                    this.props.toastNotification.success(
                        alertMessage.CONDITION_DELETE_SUCCESS
                    );
                });
            if (index === this.state.editIndex) {
                this.setState({
                    editRuleIndex: -1,
                    editIndex: -1
                });
            }
        }
    };

    /**=========================================================
     * Pre Condition End========================================
     *========================================================*/

    /**=========================================================
     * Post Condition===========================================
     *========================================================*/

    /**=========================================================
     * Post Condition End=======================================
     *========================================================*/

    addCondition = () => {
        if (this.state.isAdd) {
            return (
                <AddConditionContainer
                    toastNotification={this.props.toastNotification}
                    addCondition={this.state.addCondition}
                    selectConditionType={this.onConditionTypeChange}
                    updateCondition={this.updateAddCondition}
                    onSaveAddCondition={this.onSaveAddCondition}
                    onCancelCondition={this.onCancelAddCondition}
                    getQuestionById={this.props.getQuestionById}
                    editRuleIndex={this.state.editRuleIndex}
                    filteredQuestionSummaries={
                        this.state.filteredQuestionSummaries
                    }
                    flattenQuestionSummaries={
                        this.state.flattenQuestionSummaries
                    }
                    questions={parseQuestionSummaryToObjects(
                        this.state.flattenQuestionSummaries
                    )}
                    errorObject={this.state.addErrors}
                    editRule={this.onEditRule}
                    onCancelAddRule={this.onCancelAddRule}
                    editEnabled={this.props.editEnabled}
                    currentQuestionId={this.state.activeQuestionId}
                />
            );
        }
    };

    renderQuestionSelection = () => {
        const { activeQuestionId } = this.state;
        const { currentQuestion } = this.props;
        const activeSelection = currentQuestion.type === QTYPES.POPUP;
        if (activeSelection) {
            let questions = [
                { label: currentQuestion.title, value: currentQuestion.id }
            ];
            questions = [
                ...questions,
                ...currentQuestion.popupCards.map((card) => ({
                    label: card.title,
                    value: card.id
                }))
            ];
            const selectedValue = questions.find(
                (q) => q.value === activeQuestionId
            );
            return (
                <div className="form-group">
                    <label className="form-group__label form-group__label--block">
                        Select [Question] that you would like to attach
                        Conditions to
                    </label>
                    <Select
                        className={'form-select--react-select react-select'}
                        classNamePrefix="react-select"
                        col="col-md-12"
                        options={questions}
                        value={selectedValue}
                        onChange={(question) =>
                            this.setState(
                                { activeQuestionId: question.value },
                                this.fetchCondition
                            )
                        }
                    />
                </div>
            );
        }
    };

    render() {
        const { isAdd, isEdit, isLoading } = this.state;
        if (isLoading) {
            return <Loader isFixed={true} />;
        }
        return (
            <div className="project-add--question-builder conditions">
                <ConditionHeader />
                {this.renderQuestionSelection()}
                <Conditions
                    toastNotification={this.props.toastNotification}
                    preconditions={this.state.preconditions}
                    postconditions={this.state.postconditions}
                    editIndex={this.state.editIndex}
                    editType={this.state.editType}
                    editRuleIndex={this.state.editRuleIndex}
                    filteredQuestionSummaries={
                        this.state.filteredQuestionSummaries
                    }
                    flattenQuestionSummaries={
                        this.state.flattenQuestionSummaries
                    }
                    questions={parseQuestionSummaryToObjects(
                        this.state.flattenQuestionSummaries
                    )}
                    preConditionErrorObject={this.state.preconditionsErrors}
                    postConditionErrorObject={this.state.postconditionsErrors}
                    onSaveEditCondition={this.onSaveEditCondition}
                    updateCondition={this.updateEditCondition}
                    getQuestionById={this.props.getQuestionById}
                    editCondition={this.onEditCondition}
                    cancelCondition={this.onCancelEditCondition}
                    editRule={this.onEditRule}
                    deleteCondition={this.onDeleteConditions}
                    onCancelAddRule={this.onCancelAddRule}
                    editEnabled={this.props.editEnabled}
                    healthCondition={
                        this.props.projectHealth &&
                        this.props.projectHealth[this.state.activeQuestionId]
                    }
                    currentQuestionId={this.state.activeQuestionId}
                />
                {this.addCondition()}
                <AddConditionButton
                    onAddClick={this.onAddCondition}
                    hide={isAdd || isEdit}
                    disabled={!this.props.editEnabled}
                />
            </div>
        );
    }
}

const mapStateToProps = ({
    questionReducer,
    projectReducer,
    utilsReducer
}: IApplicationState) => ({
    questionSummaries:
        questionReducer.questionSummaries[projectReducer.currentProject.id] ||
        [],
    currentQuestionId: questionReducer.currentQuestionId,
    currentQuestion: questionReducer.currentQuestion,
    editEnabled: projectReducer.editEnabled['question'],
    projectHealth: sortHealthProblemByQuestionID(projectReducer.projectHealth, [
        HealthProblemContext.CONDITION
    ]),
    currentProject: projectReducer.currentProject,
    isEdited: utilsReducer.isEdited,
    editStatus: utilsReducer.editStatus
});

const mapDispatchToProps = (dispatch) => ({
    getQuestionById: (questionId: number, options, logic) =>
        dispatch(getQuestionByQuestionId(questionId, options)),
    getConditions: (questionId: number) => dispatch(getConditions(questionId)),
    saveConditions: (questionId: number, conditions, isPrecondition = false) =>
        dispatch(saveConditions(questionId, conditions, isPrecondition)),
    deleteConditions: (questionId: number, conditionIds) =>
        dispatch(deleteConditions(questionId, conditionIds)),
    setEditedStatus: (type: string, status?: PROJECT_EDIT_STATUS) =>
        dispatch(setEditedStatus(type, status)),
    clearEditedStatus: () => dispatch(clearEditedStatus())
});

export default NotifyHOC(
    connect(mapStateToProps, mapDispatchToProps)(ConditionContainer)
);
